import { create } from 'zustand';
import type { ProjectEvent } from '../api/projectStream.js';
import { useAuthStore } from './authStore.js';

/**
 * Store GLOBAL de trabajos de cotización en curso (run-quote y APU-IA).
 *
 * Problema que resuelve: el progreso viajaba por WebSocket pero solo QuotePage
 * lo mostraba, y al navegar fuera se perdían los eventos — al volver, la pantalla
 * arrancaba de cero. Cualquier página registra/actualiza los jobs aquí, así el
 * presupuesto retoma el avance donde iba.
 *
 * Hubo un mini-indicador con % en el topbar alimentado por este almacén; se
 * retiró el 2026-08-23 (llevaba a la pantalla vieja de análisis de precios).
 * El almacén sigue siendo necesario por lo de arriba.
 *
 * Se alimenta de:
 *  - QuotePage / ProjectWorkspacePage: reenvían sus eventos WS (ingestProjectEvent).
 *  - QuoteJobsWatcher (montado en App, en `components/TrabajosEnCurso.tsx`):
 *    mantiene un WS abierto al proyecto del job activo aunque el usuario
 *    navegue fuera.
 *  - QuoteApuIaRunPage: registra el run y lo actualiza con su polling.
 *
 * **Alcance por empresa**: los jobs pertenecen a la empresa activa. `tenantId`
 * marca de quién es el contenido de `jobs`; al cambiar de empresa el almacén se
 * limpia (mismo patrón que `usageStore.clear()` en logout/switch) para que no
 * queden avisos ni aros de avance de la empresa anterior.
 */

export type QuoteJobKind = 'quote' | 'apu_ia';
export type QuoteJobStatus = 'running' | 'done' | 'failed';

export interface QuoteJob {
  /** `${kind}:${quoteId}` — un job visible por cotización y tipo. */
  key: string;
  kind: QuoteJobKind;
  quoteId: string;
  projectId: string;
  /** Empresa a la que pertenece el job (aislamiento al cambiar de contexto). */
  tenantId: string;
  /** Nombre de la cotización/proyecto para mostrar en el pill (best-effort). */
  label?: string;
  stage: string;
  status: QuoteJobStatus;
  /** 0..100 */
  pct: number;
  message?: string;
  total?: number;
  processed?: number;
  startedAt: number;
  updatedAt: number;
  /**
   * Momento en que empezó a avanzar la fase actual de comparación.
   * Se reinicia al entrar a Paso 2 (IA) para no mezclar ritmos.
   */
  compareStartedAt?: number;
  /** processed al anclar compareStartedAt (para ETA = (done-base)/tiempo). */
  compareBaseProcessed?: number;
  /** Milisegundos estimados para terminar (null = aún sin datos / no confiable). */
  etaMs: number | null;
  /** El usuario ya vio/cerró el resultado (deja de mostrarse en el topbar). */
  dismissed: boolean;
}

interface QuoteJobsState {
  /** Empresa dueña de `jobs`. null = almacén vacío / sin sesión todavía. */
  tenantId: string | null;
  jobs: Record<string, QuoteJob>;
  upsert: (partial: Partial<QuoteJob> & { kind: QuoteJobKind; quoteId: string; projectId: string }) => void;
  /** Etiqueta un job existente (nombre del proyecto) para el pill. No crea jobs. */
  setLabel: (kind: QuoteJobKind, quoteId: string, label: string) => void;
  dismiss: (key: string) => void;
  remove: (key: string) => void;
  /** Limpia el almacén (al logout o al cambiar de empresa). */
  clear: () => void;
}

/**
 * Clave dentro del alcance de una empresa. La empresa NO va en este string:
 * `jobs` solo contiene la empresa activa (`tenantId` del store) y se vacía al
 * cambiar de contexto, así que `${kind}:${quoteId}` es único ahí dentro.
 */
function jobKey(kind: QuoteJobKind, quoteId: string): string {
  return `${kind}:${quoteId}`;
}

/** Empresa activa según la sesión. null mientras no hay sesión hidratada. */
function activeTenantId(): string | null {
  return useAuthStore.getState().tenant?.id ?? null;
}

/** Tope: la preparación lenta ya no puede inventar horas. */
const ETA_CAP_MS = 8 * 60 * 1000;

/**
 * ETA desde que arranca la comparación (processed > 0).
 * - No estima en extract / "Preparando…" (embeddings).
 * - Ritmo = conceptos hechos / tiempo desde compareStartedAt.
 * - Tope 8 min; mínimo mostrable ~5 s.
 */
function computeEta(job: QuoteJob): number | null {
  const { total, processed, compareStartedAt, message, stage } = job;
  const msg = (message ?? '').toLowerCase();

  if (stage === 'extract' || stage === 'queued') return null;
  if (msg.includes('preparando')) return null;
  if (!total || total <= 0 || processed == null || processed <= 0) return null;
  if (processed >= total) return null;
  if (!compareStartedAt) return null;

  const elapsed = Date.now() - compareStartedAt;
  if (elapsed < 800) return null;

  const base = job.compareBaseProcessed ?? 0;
  const progressed = processed - base;
  if (progressed <= 0) return null;

  const rate = progressed / elapsed; // conceptos / ms
  if (rate <= 0) return null;

  const eta = Math.round((total - processed) / rate);
  if (!Number.isFinite(eta) || eta < 0) return null;
  // Piso 5s: evita parpadeos "~1s"; techo 8 min.
  return Math.min(ETA_CAP_MS, Math.max(5_000, eta));
}

export const useQuoteJobsStore = create<QuoteJobsState>((set, get) => ({
  tenantId: null,
  jobs: {},

  upsert: (partial) => {
    const active = activeTenantId();
    const scope = get().tenantId;
    // Cambio de empresa: los jobs de la anterior se descartan aquí también, no
    // solo en AuthBootstrap — un evento del WS puede llegar tarde, ya cambiado
    // el contexto, y no debe revivir el aviso de la empresa que se dejó.
    const switched = !!active && !!scope && active !== scope;
    const tenantId = active ?? scope ?? '';
    const key = jobKey(partial.kind, partial.quoteId);
    const prev = switched ? undefined : get().jobs[key];
    const now = Date.now();
    const merged: QuoteJob = {
      key,
      kind: partial.kind,
      quoteId: partial.quoteId,
      projectId: partial.projectId,
      tenantId,
      label: partial.label ?? prev?.label,
      stage: partial.stage ?? prev?.stage ?? 'queued',
      status: partial.status ?? prev?.status ?? 'running',
      pct: partial.pct ?? prev?.pct ?? 0,
      message: partial.message ?? prev?.message,
      total: partial.total ?? prev?.total,
      processed: partial.processed ?? prev?.processed,
      startedAt: prev?.startedAt ?? now,
      updatedAt: now,
      compareStartedAt: prev?.compareStartedAt,
      compareBaseProcessed: prev?.compareBaseProcessed,
      etaMs: prev?.etaMs ?? null,
      dismissed: partial.status === 'running' ? false : (prev?.dismissed ?? false),
    };

    // Nuevo run: solo al pasar a running desde otro estado.
    const revived = prev && partial.status === 'running' && prev.status !== 'running';
    if (revived || !prev) {
      merged.compareStartedAt = undefined;
      merged.compareBaseProcessed = undefined;
      merged.etaMs = null;
      if (!prev || revived) merged.startedAt = now;
    }

    // Nuevo ancla al entrar a Paso 2 (IA) — ritmo distinto al de catálogos.
    const enteringFase2 =
      merged.status === 'running'
      && /paso 2 de 2/i.test(merged.message ?? '')
      && !!prev
      && !/paso 2 de 2/i.test(prev.message ?? '');
    if (enteringFase2) {
      merged.compareStartedAt = now;
      merged.compareBaseProcessed = merged.processed ?? 0;
      merged.etaMs = null;
    }

    // Arranque de comparación (Paso 1 / solo catálogos).
    if (
      merged.status === 'running'
      && (merged.processed ?? 0) > 0
      && !merged.compareStartedAt
      && !(merged.message ?? '').toLowerCase().includes('preparando')
    ) {
      merged.compareStartedAt = now;
      merged.compareBaseProcessed = 0;
    }

    const nextEta = merged.status === 'running' ? computeEta(merged) : null;
    // Mantener el último ETA un momento si el cálculo aún no tiene ventana
    // (evita que parpadee a vacío entre latidos).
    if (merged.status !== 'running') {
      merged.etaMs = null;
    } else if (nextEta != null) {
      merged.etaMs = nextEta;
    } else if ((merged.message ?? '').toLowerCase().includes('preparando')) {
      merged.etaMs = null;
    } else if ((merged.processed ?? 0) >= (merged.total ?? 0) && (merged.total ?? 0) > 0) {
      merged.etaMs = null;
    }
    // else: conservar prev etaMs

    if (merged.status !== 'running') merged.pct = merged.status === 'done' ? 100 : merged.pct;
    const base = switched ? {} : get().jobs;
    set({ tenantId, jobs: { ...base, [key]: merged } });
  },

  setLabel: (kind, quoteId, label) => {
    const key = jobKey(kind, quoteId);
    const prev = get().jobs[key];
    if (!prev || prev.label === label) return;
    set({ jobs: { ...get().jobs, [key]: { ...prev, label } } });
  },

  dismiss: (key) => {
    const prev = get().jobs[key];
    if (!prev) return;
    set({ jobs: { ...get().jobs, [key]: { ...prev, dismissed: true } } });
  },

  remove: (key) => {
    const next = { ...get().jobs };
    delete next[key];
    set({ jobs: next });
  },

  clear: () => set({ tenantId: null, jobs: {} }),
}));

/**
 * Alimenta el store desde cualquier evento WS de proyecto. Idempotente: varias
 * páginas pueden reenviar el mismo evento sin duplicar jobs.
 */
export function ingestProjectEvent(projectId: string, ev: ProjectEvent): void {
  if (ev.type === 'quote:progress') {
    const d = ev.data;
    const total = d.conceptsTotal;
    const processed = d.conceptsProcessed;
    const status: QuoteJobStatus =
      // `status:'done'` con stage!=done es el fin del extract-only ("Generar
      // listado de conceptos") — sin esto el pill quedaba corriendo por siempre.
      d.stage === 'done' || d.status === 'done' ? 'done'
      : d.stage === 'failed' || d.stage === 'stopped' ? 'failed'
      : 'running';
    useQuoteJobsStore.getState().upsert({
      kind: 'quote',
      quoteId: d.quoteId,
      projectId,
      stage: d.stage,
      status,
      message: d.message,
      total,
      processed,
      pct: d.pct != null
        ? Math.min(100, Math.max(0, Math.round(d.pct)))
        : total && total > 0 && processed != null
          ? Math.min(100, Math.round((processed / total) * 100))
          : undefined,
    });
    if (status === 'done') useQuoteJobsStore.getState().dismiss(`quote:${d.quoteId}`);
    return;
  }
  if (ev.type === 'apu_ia:progress') {
    const d = ev.data;
    if (!d.quoteId) return;
    // `stopped` (lo detuvo el usuario) cierra el trabajo como terminado, no como error:
    // lo generado quedó bien guardado. Sin esto el pill se quedaría en "corriendo" para
    // siempre después de un Detener.
    const status: QuoteJobStatus =
      d.stage === 'done' || d.stage === 'stopped' ? 'done'
        : d.stage === 'failed' ? 'failed'
          : 'running';
    useQuoteJobsStore.getState().upsert({
      kind: 'apu_ia',
      quoteId: d.quoteId,
      projectId,
      stage: d.stage,
      status,
      total: d.conceptos_total,
      processed: d.conceptos_procesados,
      message: d.message,
      // Si el worker manda un % por fase, se usa tal cual (avanza dentro de cada
      // concepto); si no, se calcula de procesados/total.
      pct: d.pct != null
        ? Math.min(100, Math.max(0, Math.round(d.pct)))
        : d.conceptos_total > 0
          ? Math.min(100, Math.round((d.conceptos_procesados / d.conceptos_total) * 100))
          : undefined,
    });
    if (status === 'done') useQuoteJobsStore.getState().dismiss(`apu_ia:${d.quoteId}`);
  }
}

/**
 * Job más relevante para el topbar: solo los que siguen corriendo.
 * Con `tenantId` se descartan además los de otra empresa (defensa en
 * profundidad: el almacén ya se limpia al cambiar de contexto).
 */
export function selectTopJob(
  jobs: Record<string, QuoteJob>,
  tenantId?: string | null,
): QuoteJob | null {
  const running = Object.values(jobs).filter(
    (j) => !j.dismissed && j.status === 'running' && (!tenantId || j.tenantId === tenantId),
  );
  if (running.length === 0) return null;
  return running.reduce((a, b) => (b.updatedAt > a.updatedAt ? b : a));
}
